import isString from 'lodash/isString'
import { mutate } from 'swr'
import { cache, createCacheHelper } from 'swr/_internal'
import { fetchData } from './fetch'
import { FetchHandlerParamsType } from './types'

export const STANDARD_TRANSITION_TIMEOUT = 5000

// to get useSWR to re-fetch data which it already fetched, you both need to remove the URL from the SWR cache
// (done in cacheBustURL below) and also break possible memoization of the SWR results in React's cache.
// In order to break React memoization, we need to change the string parameter passed to the useSWR hook (the cache
// key).  We will keep a number for each URL we have cached in this record, and we will increment that number when
// cacheBustURL is called to ensure that the cache key for that URL will change in the future, breaking any possible
// memoization of that value.
const breakSWRMemoExtraCache: Record<string, number> = {}

interface PutPostHandlerParamsType extends FetchHandlerParamsType {
  // the POST body
  body: Record<string, unknown> | string
}

/**
 * this is the standard fetch algorithm for making POSTs
 * @param {FetchDataParamsType} - the parameters as defined in the interface
 *   @param {String} url - the URL to fetch from
 *   @param {Object} options - additional options for the fetcher
 * @return {Promise} - the data fetching promise as returned from window.fetch
 */
export async function postData<FetchDataType>({
  body,
  options = {},
  ...rest
}: PutPostHandlerParamsType): Promise<FetchDataType> {
  const combinedOptions = {
    ...options,
    method: options.method || 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: isString(body) ? body : JSON.stringify(body)
  }
  return await fetchData({
    ...rest,
    options: combinedOptions
  })
}

// use this fetcher when you don't want to make a data call but you need to call useGet or usePost
// because of rules of hooks
export async function fetchNoop<FetchDataType>(): Promise<FetchDataType> {
  return {} as FetchDataType
}

/**
 * call this to generate a cache key
 * @param {String} urlOrCacheKey - the URL to generate a cache for, or a cache key
 * @param {Object or String} body - POST data object to be part of the cache key, optional
 * @return {String} - a cache key to use
 */
export function makeCacheKey(
  urlOrCacheKey: string,
  body: Record<string, unknown> | string = ''
): string {
  if (!urlOrCacheKey.endsWith('/')) {
    urlOrCacheKey += '/'
  }
  const serializedBody = isString(body) ? body : JSON.stringify(body)
  const breakMemoExtraCache = breakSWRMemoExtraCache[urlOrCacheKey]
  return (
    urlOrCacheKey +
    serializedBody +
    (breakMemoExtraCache ? breakMemoExtraCache.toString() : '')
  )
}

/**
 * call this to cache bust the given URL (including any varying QP or POST parameters)
 * @param {String} url - the url to remove cache keys for
 */
export function cacheBustURL(url: string): void {
  const cacheKey = makeCacheKey(url)

  mutate(keyCur => {
    // if you use a cache key for a POST with no body specified, all cache keys related to that URL will be marked stale
    // same if you use a cache key for a URL with no QP specified, all of those URL w/any QP will also be marked stale
    return isString(keyCur) && keyCur.startsWith(cacheKey)
  }, undefined)

  // also ensure you break SWR memoization of this URL or else you may still get this stale data again
  if (!breakSWRMemoExtraCache[url]) {
    breakSWRMemoExtraCache[url] = 0
  } else {
    breakSWRMemoExtraCache[url]++
  }
}

/**
 * call this to change the content of the cache at a given URL
 * @param {String} urlOrCacheKey - the URL part of the cache key, or an already built cache key (no-op)
 * @param {Object or String} body - POST data object to be part of the cache key, optional
 * @param {Object} data - the data to replace in the cache
 */
export function replaceCacheURL(
  urlOrCacheKey: string,
  body: Record<string, unknown> | string = '',
  data: Record<string, unknown>
): void {
  const cacheKey = makeCacheKey(urlOrCacheKey, body)
  mutate(cacheKey, data)
}

/**
 * this is a function to get a URL and insert the result in the cache.  This can be used to "prime" data to avoid
 * loading states in SWR
 * @param {String} url - the URL to GET
 * @param {String} context - the context of the GET, for debugging purposes
 * @param {Boolean} force - whether to force the re-cache even if data exists
 * @return {Object} - the data from the GET
 */
export async function getAndCache(
  url: string,
  context: string,
  force = false
): Promise<Record<string, unknown>> {
  const cacheKey = makeCacheKey(url)
  const [getFromSWRCache] = createCacheHelper(cache, cacheKey)
  let data = getFromSWRCache().data
  if (!data || force) {
    const cacheText = `[swr] getAndCache ${force ? 'forced' : 'not cached'}`
    const forceExtra = force ? `was cached: ${!!data}` : ''
    console.log(cacheText, context, url, forceExtra)

    try {
      data = await fetchData({ url })
      if (force) {
        // if we ae forcing a cache replacement, then cache bust anything that was similar (different QP) too
        cacheBustURL(url)
      }

      mutate(cacheKey, data)
    } catch (err) {
      // silent failure if we can't GET when trying to cache.  Return the error if someone is inspecting the response
      data = err
    }
  }
  return data
}

/**
 * this is a function to get a URL and insert the result in the cache.  This can be used to "prime" data to avoid
 * loading states in SWR
 * @param {String} url - the URL to POST to
 * @param {Object} body - the POST body
 * @param {String} context - the context of the POST, for debugging purposes
 * @param {Boolean} force - whether to force the re-cache even if data exists
 * @return {Object} - the data from the POST
 */
export async function postAndCache(
  url: string,
  body: Record<string, unknown>,
  context: string,
  force = false
): Promise<Record<string, unknown>> {
  const serializedBody = JSON.stringify(body)
  const cacheKey = makeCacheKey(url, serializedBody)
  const [getFromSWRCache] = createCacheHelper(cache, cacheKey)
  let data = getFromSWRCache().data
  if (!data || force) {
    const cacheText = `[swr] postAndCache ${force ? 'forced' : 'not cached'}`
    const forceExtra = force ? `was cached: ${!!data}` : ''
    console.log(cacheText, context, url, body, forceExtra)

    try {
      data = await postData({ url, body: serializedBody })
      if (force) {
        // if we ae forcing a cache replacement, then cache bust anything that was similar (different post params) too
        // this was fix for MRD-1661
        cacheBustURL(url)
      }

      mutate(cacheKey, data)
    } catch (err) {
      // silent failure if we can't POST when trying to cache.  Return the error if someone is inspecting the response
      data = err
    }
  }
  return data
}
